/**
 * Checkout Block - Frontend (Strongly-Typed)
 *
 * @package Enterprise\BookingSystem\Blocks
 */

/**
 * WordPress dependencies
 */
import { useSelect, useDispatch } from "@wordpress/data";
import { createRoot } from "@wordpress/element";
import { useEffect, useState, useRef, useMemo } from "@wordpress/element";
/**
 * External dependencies
 */
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
/**
 * Internal dependencies
 */
import { StripePaymentForm } from "../../components/StripePayment";
import { OrderSummary } from "../../components/OrderSummary";
import type { StripePaymentHandle } from "../../components/StripePayment";
import type {
	BillingAddress,
	ShippingAddress,
} from "../../data/checkout/types";
import type {
	CheckoutStoreSelectors,
	CheckoutStoreActions,
} from "../../types/store.types";
import type { CartFee, CartItem } from "../../types/cart.types";
import { usePageUrls } from "../../hooks/useSettings";
import { cartApiService } from "../../services/api";
import type { ServiceAreaResult } from "../../services/api/cart-api.service";
import { tracker } from "../../services/analytics";
import type { AnalyticsItem } from "../../services/analytics";

function checkoutCurrency(): string {
	return ( typeof ebsConfig !== 'undefined' ? ( ebsConfig.currency ?? '' ) : '' ) || 'USD';
}

function cartItemsToAnalytics( items: CartItem[] ): AnalyticsItem[] {
	return items.map( ( item ) => ( {
		item_id: String( item.product_id ),
		item_name: item.name,
		item_variant: item.variant_name,
		price: item.price / 100,
		quantity: item.quantity,
	} ) );
}

const CHECKOUT_STORE = "ebs/checkout";

type CheckoutProps = {
	readonly className?: string;
};

const Checkout = ({ className = "" }: CheckoutProps) => {
	// Get configured page URLs
	const { shopUrl } = usePageUrls();

	// Select checkout data with strong typing
	const {
		currentStep,
		billingAddress,
		shippingAddress,
		shipToBilling,
		cartItems,
		totals,
		errors,
		isProcessing,
		isValidating,
		isLoaded,
		canProceed,
		eventDetails,
	} = useSelect((select) => {
		const store = select(CHECKOUT_STORE) as CheckoutStoreSelectors;
		return {
			currentStep: store.getCurrentStep(),
			billingAddress: store.getBillingAddress(),
			shippingAddress: store.getShippingAddress(),
			shipToBilling: store.isShipToBilling(),
			cartItems: store.getCartItems(),
			totals: store.getTotals(),
			errors: store.getErrors(),
			isProcessing: store.isProcessing(),
			isValidating: store.isValidating(),
			isLoaded: store.isLoaded(),
			canProceed: store.canProceedToNextStep(),
			eventDetails: store.getEventDetails(),
		};
	}, []);

	// Dispatch actions with strong typing
	const dispatch = useDispatch(CHECKOUT_STORE) as CheckoutStoreActions;

	// ── Event Details pre-step ───────────────────────────────────────────────
	const [isEventDetailsStep, setIsEventDetailsStep] = useState(true);
	const [eventDate, setEventDate] = useState("");
	const [eventZip, setEventZip] = useState("");
	const [zipCheckResult, setZipCheckResult] = useState<null | ServiceAreaResult>(null);
	const [isCheckingZip, setIsCheckingZip] = useState(false);
	const [eventDetailsErrors, setEventDetailsErrors] = useState<Record<string, string>>({});
	const [isSavingEventDetails, setIsSavingEventDetails] = useState(false);

	// Tracks whether the next ZIP check was triggered by the initial cart load
	// (vs. the user typing a new ZIP). Used to gate automatic step restoration.
	const isLoadZipCheck = useRef(false);

	// Pre-populate event details from checkout store once cart is loaded.
	// Does NOT skip the event-details step here — the ZIP check (below) does
	// that after confirming the stored ZIP is still within a service area.
	useEffect(() => {
		if (!isLoaded) return;
		if (eventDetails.event_date) {
			setEventDate(eventDetails.event_date);
		}
		// city field stores the ZIP in the new model.
		if (eventDetails.city && /^\d{5}$/.test(eventDetails.city)) {
			isLoadZipCheck.current = true;
			setEventZip(eventDetails.city);
		}
	// Only run once after initial cart load.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded]);

	// Validate ZIP against service areas on blur.
	const handleZipBlur = () => {
		if (!/^\d{5}$/.test(eventZip)) {
			setZipCheckResult(null);
			return;
		}
	setIsCheckingZip(true);
	void cartApiService.checkServiceArea(eventZip)
		.then((data) => { setZipCheckResult(data); })
		.catch(() => { setZipCheckResult(null); })
		.finally(() => { setIsCheckingZip(false); });
	};

	// Run ZIP check when eventZip is set (covers both on-load pre-fill and user blur).
	// On load: also restores or enforces the event-details step based on the result —
	// if the stored ZIP is no longer serviceable, the user is sent back to fix it.
	useEffect(() => {
		if (eventZip && /^\d{5}$/.test(eventZip) && !zipCheckResult) {
			const isOnLoad = isLoadZipCheck.current;
			isLoadZipCheck.current = false;
		setIsCheckingZip(true);
		void cartApiService.checkServiceArea(eventZip)
			.then((data) => {
				setZipCheckResult(data);
				if (isOnLoad) {
					// Restore the post-event-details view only if the ZIP is still
					// within a service area. If not, keep the event-details step
					// visible so the customer must correct or re-confirm their ZIP.
					if (data.serviceable && eventDate) {
						setIsEventDetailsStep(false);
					}
				}
			})
			.catch(() => { setZipCheckResult(null); })
			.finally(() => { setIsCheckingZip(false); });
		}
	// Re-run only when eventZip changes.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventZip]);

	// Keep the order summary fees in sync whenever the ZIP check result changes.
	// Uses the tax_rate + delivery_fee from the check response to compute fees
	// locally — no extra API call needed.
	useEffect(() => {
		if (!isLoaded) return;
		if (zipCheckResult?.serviceable) {
			dispatch.refreshTotalsFromZip(
				zipCheckResult.tax_rate,
				zipCheckResult.delivery_fee,
			);
		} else if (zipCheckResult !== null) {
			// ZIP is invalid / not serviceable — clear area fees.
			dispatch.refreshTotalsFromZip(0, 0);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [zipCheckResult, isLoaded]);

	const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);
	const maxDateStr = useMemo(
		() => new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
		[],
	);

	const validateEventDetails = (): boolean => {
		const errs: Record<string, string> = {};
		if (!eventDate) {
			errs.eventDate = "Event date is required";
		} else {
			const d = new Date(eventDate + "T00:00:00"); // local midnight, not UTC
			const today = new Date(); today.setHours(0, 0, 0, 0);
			if (d < today) errs.eventDate = "Event date must be in the future";
		}
		if (!/^\d{5}$/.test(eventZip)) {
			errs.eventZip = "A valid 5-digit ZIP code is required";
		} else if (!zipCheckResult?.serviceable) {
			errs.eventZip = zipCheckResult?.serviceable === false
				? zipCheckResult.message
				: "Please verify your ZIP code";
		}
		setEventDetailsErrors(errs);
		return Object.keys(errs).length === 0;
	};

	const handleEventDetailsContinue = () => {
		if (!validateEventDetails()) return;
		void (async () => {
			try {
				setIsSavingEventDetails(true);
				// Store ZIP as the city field for backward compat with the cart schema.
				// The area label (or empty string) goes into state.
				const areaLabel = zipCheckResult?.serviceable ? (zipCheckResult.label ?? "") : "";
				const updatedCart = await cartApiService.updateEventDetails({
					event_date: eventDate,
					state: areaLabel,
					city: eventZip,
				});
				// Apply the server's authoritative totals (includes all fees) directly
				// from the updateEventDetails response — no extra fetchCart needed.
				dispatch.applyCartTotals(updatedCart);
				// Pre-fill billing postcode so the customer doesn't have to re-enter it.
				dispatch.updateCustomerInfo({ postcode: eventZip } as Partial<BillingAddress>);
				setIsEventDetailsStep(false);
				globalThis.window.scrollTo({ top: 0, behavior: "smooth" });
			} catch {
				setEventDetailsErrors({ general: "Failed to save event details. Please try again." });
			} finally {
				setIsSavingEventDetails(false);
			}
		})();
	};

	// Stripe state
	const [stripePromise, setStripePromise] = useState<null | ReturnType<typeof loadStripe>>(null);
	const [clientSecret, setClientSecret] = useState<null | string>(null);
	const [paymentError, setPaymentError] = useState<null | string>(null);
	const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
	
	// Ref for Stripe payment form
	const stripePaymentRef = useRef<null | StripePaymentHandle>(null);

	// Load checkout data on mount
	useEffect(() => {
		if (!isLoaded) {
			dispatch.fetchCart();
		}
	}, [dispatch, isLoaded]);

	// Load Stripe and create Payment Intent when reaching Step 3 (only if payment needed)
	const hasTrackedPaymentInfo = useRef( false );
	useEffect(() => {
		// Create Payment Intent when reaching Step 3 (checkout store step 3 = Payment step).
		if (!isEventDetailsStep && currentStep === 3 && !clientSecret && totals.needsPayment) {
			dispatch.createPaymentIntent();
		}

		// Track add_payment_info once when the payment step is first reached.
		if ( ! isEventDetailsStep && currentStep === 3 && ! hasTrackedPaymentInfo.current ) {
			hasTrackedPaymentInfo.current = true;
			tracker.addPaymentInfo(
				checkoutCurrency(),
				totals.total / 100,
				cartItemsToAnalytics( cartItems ),
			);
		}
	}, [currentStep, clientSecret, totals.needsPayment, totals.total, dispatch]);

	// Initialize Stripe with publishable key from store
	const {
		stripePublishableKey,
		stripeClientSecret,
		isLoadingPaymentIntent,
	} = useSelect((select) => {
		const store = select(CHECKOUT_STORE) as CheckoutStoreSelectors;
		return {
			stripePublishableKey: store.getStripePublishableKey(),
			stripeClientSecret: store.getStripeClientSecret(),
			isLoadingPaymentIntent: store.isLoadingPaymentIntent(),
		};
	}, []);

	useEffect(() => {
		if (stripePublishableKey && !stripePromise) {
			setStripePromise(loadStripe(stripePublishableKey));
		}
	}, [stripePublishableKey, stripePromise]);

	useEffect(() => {
		if (stripeClientSecret) {
			setClientSecret(stripeClientSecret);
		}
	}, [stripeClientSecret]);

	// Handle billing address changes
	const handleBillingChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
	) => {
		const { name, value } = e.target;
		dispatch.updateCustomerInfo({
			[name]: value,
		} as Partial<BillingAddress>);
	};

	// Handle shipping address changes
	const handleShippingChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
	) => {
		const { name, value } = e.target;
		dispatch.updateShippingAddress({
			[name]: value,
		} as Partial<ShippingAddress>);
	};

	// Handle next step
	const handleNext = () => {
		if ( currentStep === 1 ) {
			tracker.addShippingInfo(
				checkoutCurrency(),
				totals.total / 100,
				cartItemsToAnalytics( cartItems ),
			);
		}
		dispatch.proceedToNextStep();
		globalThis.window.scrollTo({ top: 0, behavior: "smooth" });
	};

	// Handle previous step
	const handlePrev = () => {
		dispatch.prevStep();
		globalThis.window.scrollTo({ top: 0, behavior: "smooth" });
	};

	// Handle submit with proper payment flow
	const handleSubmit = () => {
		// Clear previous errors
		setPaymentError(null);

		// Use void to handle async operation without returning promise to onClick
		void (async () => {
			try {
				// For orders that need payment, confirm payment first
			if (totals.needsPayment && clientSecret && clientSecret !== 'test_zero_amount') {
				setIsConfirmingPayment(true);

					// Confirm payment via Stripe
					if (!stripePaymentRef.current) {
						throw new Error("Payment form is not ready");
					}

				await stripePaymentRef.current.confirmPayment();
			}

				// Payment is confirmed (or not needed), now submit the order
				// The paymentIntentId will be sent to backend via the store
				dispatch.submitCheckout();

		} catch (error) {
			setIsConfirmingPayment(false);
				
				// Error is already shown via onError callback in StripePaymentForm
				// But ensure isProcessing is reset if needed
			}
		})();
	};

	// Show loading state
	if (!isLoaded) {
		return (
			<div className={`ebs-checkout ${className}`}>
				<div className="ebs-checkout-loading">
					<p>Loading checkout...</p>
				</div>
			</div>
		);
	}

	// Show empty cart state
	if (cartItems.length === 0) {
		return (
			<div className={`ebs-checkout ${className}`}>
				<div className="ebs-checkout-empty">
					<p>Your cart is empty. Please add items before checking out.</p>
					<a className="ebs-checkout__button" href={shopUrl}>
						Continue Shopping
					</a>
				</div>
			</div>
		);
	}

	return (
		<div className={`ebs-checkout ${className}`}>
			<div className="ebs-checkout__container">
				{/* Left Column: Steps */}
				<div className="ebs-checkout__steps">
					<h1 className="ebs-checkout__title">Checkout</h1>

				{/* Progress Steps */}
				<div className="ebs-checkout__progress">
					{/* Step 1: Event Details — clickable when completed */}
					<div
						className={`ebs-checkout__step ${
							isEventDetailsStep ? "active" : "completed"
						}`}
						onClick={!isEventDetailsStep ? () => { setIsEventDetailsStep(true); } : undefined}
						role={!isEventDetailsStep ? "button" : undefined}
						tabIndex={!isEventDetailsStep ? 0 : undefined}
						onKeyDown={!isEventDetailsStep ? (e) => { if (e.key === "Enter" || e.key === " ") setIsEventDetailsStep(true); } : undefined}
						aria-label={!isEventDetailsStep ? "Go back to Event Details" : undefined}
					>
						<div className="ebs-checkout__step-indicator">
							{!isEventDetailsStep ? (
								<span className="ebs-checkout__step-check">✓</span>
							) : (
								<span className="ebs-checkout__step-number">1</span>
							)}
						</div>
						<div className="ebs-checkout__step-label">
							<div className="ebs-checkout__step-title">Step 1</div>
							<div className="ebs-checkout__step-subtitle">Event Details</div>
						</div>
					</div>

					<div className="ebs-checkout__progress-line" />

					{/* Step 2: Billing — clickable when completed */}
					{(() => {
						const billingCompleted = !isEventDetailsStep && currentStep > 1;
						return (
							<div
								className={`ebs-checkout__step ${
									!isEventDetailsStep && currentStep === 1
										? "active"
										: billingCompleted
										? "completed"
										: ""
								}`}
								onClick={billingCompleted ? () => { dispatch.setStep(1); } : undefined}
								role={billingCompleted ? "button" : undefined}
								tabIndex={billingCompleted ? 0 : undefined}
								onKeyDown={billingCompleted ? (e) => { if (e.key === "Enter" || e.key === " ") dispatch.setStep(1); } : undefined}
								aria-label={billingCompleted ? "Go back to Party Information" : undefined}
							>
								<div className="ebs-checkout__step-indicator">
									{billingCompleted ? (
										<span className="ebs-checkout__step-check">✓</span>
									) : (
										<span className="ebs-checkout__step-number">2</span>
									)}
								</div>
								<div className="ebs-checkout__step-label">
									<div className="ebs-checkout__step-title">Step 2</div>
									<div className="ebs-checkout__step-subtitle">Party Information</div>
								</div>
							</div>
						);
					})()}

					<div className="ebs-checkout__progress-line" />

					{/* Step 3: Delivery — clickable when completed */}
					{(() => {
						const deliveryCompleted = !isEventDetailsStep && currentStep > 2;
						return (
							<div
								className={`ebs-checkout__step ${
									!isEventDetailsStep && currentStep === 2
										? "active"
										: deliveryCompleted
										? "completed"
										: ""
								}`}
								onClick={deliveryCompleted ? () => { dispatch.setStep(2); } : undefined}
								role={deliveryCompleted ? "button" : undefined}
								tabIndex={deliveryCompleted ? 0 : undefined}
								onKeyDown={deliveryCompleted ? (e) => { if (e.key === "Enter" || e.key === " ") dispatch.setStep(2); } : undefined}
								aria-label={deliveryCompleted ? "Go back to Delivery" : undefined}
							>
								<div className="ebs-checkout__step-indicator">
									{deliveryCompleted ? (
										<span className="ebs-checkout__step-check">✓</span>
									) : (
										<span className="ebs-checkout__step-number">3</span>
									)}
								</div>
								<div className="ebs-checkout__step-label">
									<div className="ebs-checkout__step-title">Step 3</div>
									<div className="ebs-checkout__step-subtitle">Delivery</div>
								</div>
							</div>
						);
					})()}

					<div className="ebs-checkout__progress-line" />

					{/* Step 4: Payment — not clickable */}
					<div
						className={`ebs-checkout__step ${
							!isEventDetailsStep && currentStep === 3 ? "active" : ""
						}`}
					>
						<div className="ebs-checkout__step-indicator">
							<span className="ebs-checkout__step-number">4</span>
						</div>
						<div className="ebs-checkout__step-label">
							<div className="ebs-checkout__step-title">Step 4</div>
							<div className="ebs-checkout__step-subtitle">Payment</div>
						</div>
					</div>
				</div>

					{/* Error Display */}
					{(Boolean(errors.general) || Boolean(paymentError)) && (
						<div
							className="ebs-checkout__error"
							role="alert"
						>
							{Boolean(errors.general) && <div>{errors.general}</div>}
							{Boolean(paymentError) && (
								<div className="ebs-checkout__payment-error">
									<strong>Payment Error:</strong> {paymentError}
								</div>
							)}
						</div>
					)}

				{/* Event Details pre-step */}
				{isEventDetailsStep ? (
					<div className="ebs-checkout__form-step">
						<h2 className="ebs-checkout__form-title">Event Details</h2>
						<p className="ebs-checkout__step-description">
							Tell us about your event so we can prepare your booking.
						</p>

						{eventDetailsErrors.general ? (
							<div className="ebs-checkout__error" role="alert">
								{eventDetailsErrors.general}
							</div>
						) : null}

						<div className="ebs-checkout__form-field">
							<label htmlFor="event_date">Event Date <span aria-label="required">*</span></label>
							<input
								id="event_date"
								type="date"
								value={eventDate}
								min={todayStr}
								max={maxDateStr}
								onChange={(e) => { setEventDate(e.target.value); }}
							/>
							{eventDetailsErrors.eventDate ? (
								<span className="ebs-checkout__field-error">{eventDetailsErrors.eventDate}</span>
							) : null}
						</div>

						<div className="ebs-checkout__form-field">
							<label htmlFor="event_zip">Event ZIP Code <span aria-label="required">*</span></label>
							<input
								id="event_zip"
								type="text"
								inputMode="numeric"
								maxLength={5}
								value={eventZip}
								placeholder="e.g. 10001"
								onChange={(e) => {
									const v = e.target.value.replace(/\D/g, "").slice(0, 5);
									setEventZip(v);
									setZipCheckResult(null);
								}}
								onBlur={handleZipBlur}
							/>
							{isCheckingZip ? (
								<span className="ebs-checkout__field-hint">Checking…</span>
							) : zipCheckResult ? (
								zipCheckResult.serviceable ? (
									<span className="ebs-checkout__field-success">
										✓ We service this area
										{zipCheckResult.label ? ` — ${zipCheckResult.label}` : ""}
									</span>
								) : (
									<span className="ebs-checkout__field-error">{zipCheckResult.message}</span>
								)
							) : null}
							{eventDetailsErrors.eventZip ? (
								<span className="ebs-checkout__field-error">{eventDetailsErrors.eventZip}</span>
							) : null}
						</div>

						<button
							className="ebs-checkout__button ebs-checkout__button--primary"
							onClick={handleEventDetailsContinue}
							disabled={isSavingEventDetails}
							type="button"
						>
							{isSavingEventDetails ? "Saving..." : "Continue"}
						</button>
					</div>
				) : null}

				{/* Step 1: Billing Information */}
				{!isEventDetailsStep && currentStep === 1 ? (
						<div className="ebs-checkout__form-step">
							<h2 className="ebs-checkout__form-title">
								Billing Information
							</h2>

							<div className="ebs-checkout__form-row">
								<div className="ebs-checkout__form-field">
									<label htmlFor="billing_first_name">First name</label>
									<input
										id="billing_first_name"
										type="text"
										name="first_name"
										value={billingAddress.first_name}
										onChange={handleBillingChange}
										placeholder="Enter your first name"
									/>
									{errors.first_name ? (
										<span className="ebs-checkout__field-error">
											{errors.first_name}
										</span>
									) : null}
								</div>
								<div className="ebs-checkout__form-field">
									<label htmlFor="billing_last_name">Last name</label>
									<input
										id="billing_last_name"
										type="text"
										name="last_name"
										value={billingAddress.last_name}
										onChange={handleBillingChange}
										placeholder="Enter your last name"
									/>
									{errors.last_name ? (
										<span className="ebs-checkout__field-error">
											{errors.last_name}
										</span>
									) : null}
								</div>
							</div>

							<div className="ebs-checkout__form-field">
								<label htmlFor="billing_email">Email</label>
								<input
									id="billing_email"
									type="email"
									name="email"
									value={billingAddress.email}
									onChange={handleBillingChange}
									placeholder="Enter your email"
								/>
								{errors.email ? (
									<span className="ebs-checkout__field-error">
										{errors.email}
									</span>
								) : null}
							</div>

							<div className="ebs-checkout__form-field">
								<label htmlFor="billing_phone">Phone</label>
								<input
									id="billing_phone"
									type="tel"
									name="phone"
									value={billingAddress.phone}
									onChange={handleBillingChange}
									placeholder="+1 (___) ___-____"
								/>
								{errors.phone ? (
									<span className="ebs-checkout__field-error">
										{errors.phone}
									</span>
								) : null}
							</div>

							<div className="ebs-checkout__form-field">
								<label htmlFor="billing_address_1">Address</label>
								<input
									id="billing_address_1"
									type="text"
									name="address_1"
									value={billingAddress.address_1}
									onChange={handleBillingChange}
									placeholder="Street address"
								/>
								{errors.address_1 ? (
									<span className="ebs-checkout__field-error">
										{errors.address_1}
									</span>
								) : null}
							</div>

							<div className="ebs-checkout__form-field">
								<input
									id="billing_address_2"
									type="text"
									name="address_2"
									value={billingAddress.address_2}
									onChange={handleBillingChange}
									placeholder="Apartment, suite, unit, etc. (optional)"
								/>
							</div>

							<div className="ebs-checkout__form-row">
								<div className="ebs-checkout__form-field">
									<label htmlFor="billing_city">City</label>
									<input
										id="billing_city"
										type="text"
										name="city"
										value={billingAddress.city}
										onChange={handleBillingChange}
										placeholder="City"
									/>
									{errors.city ? (
										<span className="ebs-checkout__field-error">
											{errors.city}
										</span>
									) : null}
								</div>
								<div className="ebs-checkout__form-field">
									<label htmlFor="billing_state">State</label>
									<input
										id="billing_state"
										type="text"
										name="state"
										value={billingAddress.state}
										onChange={handleBillingChange}
										placeholder="State"
									/>
									{errors.state ? (
										<span className="ebs-checkout__field-error">
											{errors.state}
										</span>
									) : null}
								</div>
							</div>

							<div className="ebs-checkout__form-row">
								<div className="ebs-checkout__form-field">
									<label htmlFor="billing_postcode">Postal Code</label>
									<input
										id="billing_postcode"
										type="text"
										name="postcode"
										value={billingAddress.postcode}
										onChange={handleBillingChange}
										placeholder="Postal code"
									/>
									{errors.postcode ? (
										<span className="ebs-checkout__field-error">
											{errors.postcode}
										</span>
									) : null}
								</div>
								<div className="ebs-checkout__form-field">
									<label htmlFor="billing_country">Country</label>
									<select
										id="billing_country"
										name="country"
										value={billingAddress.country}
										onChange={handleBillingChange}
									>
										<option value="US">United States</option>
										<option value="CA">Canada</option>
									</select>
								</div>
							</div>

					<div className="ebs-checkout__form-actions">
						<button
							className="ebs-checkout__button ebs-checkout__button--secondary"
							onClick={() => { setIsEventDetailsStep(true); }}
							type="button"
						>
							Back
						</button>
						<button
							className="ebs-checkout__button ebs-checkout__button--primary"
							onClick={handleNext}
							disabled={!canProceed}
							type="button"
						>
							Continue
						</button>
					</div>
				</div>
			) : null}

			{/* Step 3: Delivery Information */}
				{!isEventDetailsStep && currentStep === 2 ? (
						<div className="ebs-checkout__form-step">
							<h2 className="ebs-checkout__form-title">
								Delivery Information
							</h2>
							<p className="ebs-checkout__step-description">
								Where will the photobooth be set up for your event?
							</p>

							<div className="ebs-checkout__checkbox">
								<input
									type="checkbox"
									id="ship_to_billing"
									checked={shipToBilling}
									onChange={(e) =>
										{ dispatch.setShipToBilling(e.target.checked); }
									}
								/>
								<label htmlFor="ship_to_billing">
									Same as billing address
								</label>
							</div>

							{shipToBilling ? null : (
								<>
									<h3 className="ebs-checkout__form-subtitle">
										Delivery Address
									</h3>

									<div className="ebs-checkout__form-row">
										<div className="ebs-checkout__form-field">
											<label htmlFor="shipping_first_name">
												First name
											</label>
											<input
												id="shipping_first_name"
												type="text"
												name="first_name"
												value={shippingAddress.first_name}
												onChange={handleShippingChange}
												placeholder="First name"
											/>
										</div>
										<div className="ebs-checkout__form-field">
											<label htmlFor="shipping_last_name">
												Last name
											</label>
											<input
												id="shipping_last_name"
												type="text"
												name="last_name"
												value={shippingAddress.last_name}
												onChange={handleShippingChange}
												placeholder="Last name"
											/>
										</div>
									</div>

									<div className="ebs-checkout__form-field">
										<label htmlFor="shipping_address_1">
											Address
										</label>
										<input
											id="shipping_address_1"
											type="text"
											name="address_1"
											value={shippingAddress.address_1}
											onChange={handleShippingChange}
											placeholder="Street address"
										/>
									</div>

									<div className="ebs-checkout__form-row">
										<div className="ebs-checkout__form-field">
											<label htmlFor="shipping_city">City</label>
											<input
												id="shipping_city"
												type="text"
												name="city"
												value={shippingAddress.city}
												onChange={handleShippingChange}
												placeholder="City"
											/>
										</div>
										<div className="ebs-checkout__form-field">
											<label htmlFor="shipping_state">
												State
											</label>
											<input
												id="shipping_state"
												type="text"
												name="state"
												value={shippingAddress.state}
												onChange={handleShippingChange}
												placeholder="State"
											/>
										</div>
									</div>

									<div className="ebs-checkout__form-row">
										<div className="ebs-checkout__form-field">
											<label htmlFor="shipping_postcode">
												Postal Code
											</label>
											<input
												id="shipping_postcode"
												type="text"
												name="postcode"
												value={shippingAddress.postcode}
												onChange={handleShippingChange}
												placeholder="Postal code"
											/>
										</div>
										<div className="ebs-checkout__form-field">
											<label htmlFor="shipping_country">
												Country
											</label>
											<select
												id="shipping_country"
												name="country"
												value={shippingAddress.country}
												onChange={handleShippingChange}
											>
												<option value="">Select</option>
												<option value="US">
													United States
												</option>
												<option value="CA">Canada</option>
												<option value="GB">
													United Kingdom
												</option>
											</select>
										</div>
									</div>
								</>
							)}

							<div className="ebs-checkout__form-actions">
								<button
									className="ebs-checkout__button ebs-checkout__button--secondary"
									onClick={handlePrev}
									type="button"
								>
									Back
								</button>
								<button
									className="ebs-checkout__button ebs-checkout__button--primary"
									onClick={handleNext}
									disabled={!canProceed || isValidating}
									type="button"
								>
									{isValidating ? "Saving..." : "Continue"}
								</button>
							</div>
						</div>
					) : null}

				{/* Step 3: Payment */}
				{!isEventDetailsStep && currentStep === 3 ? (
						<div className="ebs-checkout__form-step">
							<h2 className="ebs-checkout__form-title">
								Payment
							</h2>

						{totals.needsPayment ? isLoadingPaymentIntent ? (
								<div className="ebs-checkout__loading">
									<span className="spinner" /> Preparing
									payment...
								</div>
							) : stripePromise && clientSecret && clientSecret !== 'test_zero_amount' ? (
								<Elements
									stripe={stripePromise}
									options={{ clientSecret }}
								>
									<StripePaymentForm
										ref={stripePaymentRef}
										amount={totals.total}
										currency="USD"
									onSuccess={(confirmedPaymentIntentId: string) => {
										dispatch.setPaymentIntentId(confirmedPaymentIntentId);
											setIsConfirmingPayment(false);
										}}
									onError={(error: string) => {
										setPaymentError(error);
											setIsConfirmingPayment(false);
											tracker.paymentError( error, 'card' );
										}}
										isProcessing={isProcessing || isConfirmingPayment}
									/>
								</Elements>
							) : (
								<div className="ebs-checkout__error">
									<p>Unable to load payment form.</p>
									{paymentError ? <p>{paymentError}</p> : null}
									{stripePublishableKey ? null : (
										<p className="ebs-checkout__error-detail">
											Stripe is not configured. Please
											contact support.
										</p>
									)}
								</div>
							) : (
								<div className="ebs-checkout__zero-payment">
									<p>
										✅ This is a free order. Click Complete
										Order below to finish.
									</p>
								</div>
							)}

							<div className="ebs-checkout__form-actions">
								<button
									className="ebs-checkout__button ebs-checkout__button--secondary"
									onClick={handlePrev}
									type="button"
									disabled={isProcessing || isConfirmingPayment}
								>
									Back
								</button>
								<button
									className="ebs-checkout__button ebs-checkout__button--primary"
									onClick={handleSubmit}
									disabled={isProcessing || isConfirmingPayment}
									type="button"
								>
									{isConfirmingPayment
										? "Authorizing Payment..."
										: isProcessing
										? "Processing Order..."
										: "Complete Order"}
								</button>
							</div>
						</div>
					) : null}
				</div>

				{/* Right Column: Order Summary */}
				<div className="ebs-checkout__sidebar">
					<div className="ebs-checkout__summary">
						<h3 className="ebs-checkout__summary-title">
							Order Summary
						</h3>

					{/* Event details summary */}
				{(eventDate || eventZip) ? (
					<div className="ebs-checkout__event-summary">
						{eventDate ? (
							<div className="ebs-checkout__event-detail">
								<span className="ebs-checkout__event-label">Date:</span>
								<span>{new Date(eventDate + "T12:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
							</div>
						) : null}
						{eventZip ? (
							<div className="ebs-checkout__event-detail">
								<span className="ebs-checkout__event-label">ZIP:</span>
								<span>
									{eventZip}
									{zipCheckResult?.serviceable && zipCheckResult.label
										? ` — ${zipCheckResult.label}`
										: ""}
								</span>
							</div>
						) : null}
					</div>
				) : null}

					{cartItems.map((item) => (
						<div
							key={item.id}
							className="ebs-checkout__summary-item"
						>
							<div className="ebs-checkout__summary-item-content">
								<h4 className="ebs-checkout__summary-item-name">
									{item.name}
								</h4>
								<div className="ebs-checkout__summary-item-meta">
									<span>Qty: {item.quantity}</span>
									{item.variant_name ? (
										<span>
											Variant: {item.variant_name}
										</span>
									) : null}
								</div>
							</div>
							<div className="ebs-checkout__summary-item-price">
								{item.formatted_total}
							</div>
						</div>
					))}

						<OrderSummary
							subtotal={totals.formatted_subtotal}
							fees={totals.fees as CartFee[]}
							total={totals.formatted_total}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

// Initialize checkout blocks on the page
function initCheckoutBlock() {
	const checkoutBlocks = document.querySelectorAll(
		".wp-block-ebs-checkout"
	);

	for (const block of checkoutBlocks) {
		const root = createRoot(block);
		root.render(
			<Checkout className="wp-block-ebs-checkout" />
		);
	}
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initCheckoutBlock);
} else {
	initCheckoutBlock();
}
